import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';

/**
 * WebSocket event types from the backend
 */
export enum EventType {
  // Agent events
  AGENT_CREATED = 'agent.created',
  AGENT_UPDATED = 'agent.updated',
  AGENT_DELETED = 'agent.deleted',
  AGENT_STATUS_CHANGED = 'agent.status_changed',

  // Execution events
  AGENT_EXECUTION_STARTED = 'agent.execution.started',
  AGENT_EXECUTION_COMPLETED = 'agent.execution.completed',
  AGENT_EXECUTION_FAILED = 'agent.execution.failed',

  // System events
  CONNECTION_ESTABLISHED = 'connection.established',
  HEARTBEAT = 'heartbeat',
  ERROR = 'error',
}

/**
 * WebSocket event payload structure
 */
export interface WebSocketEvent {
  type: EventType | string;
  payload: Record<string, unknown>;
  timestamp: string;
  agent_id?: string;
}

/**
 * Connection state type
 */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Options for the useWebSocket hook
 */
export interface UseWebSocketOptions {
  /** Callback when a message is received */
  onMessage?: (event: WebSocketEvent) => void;
  /** Callback when connection is established */
  onConnect?: () => void;
  /** Callback when connection is closed */
  onDisconnect?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Event) => void;
  /** Whether to automatically reconnect on disconnect (default: true) */
  reconnect?: boolean;
  /** Base reconnection interval in ms (default: 3000) */
  reconnectInterval?: number;
  /** Maximum reconnection attempts (default: 5) */
  maxReconnectAttempts?: number;
  /** Heartbeat interval in ms (default: 30000) */
  heartbeatInterval?: number;
}

/**
 * Return type for the useWebSocket hook
 */
export interface UseWebSocketReturn {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Current connection state */
  connectionState: ConnectionState;
  /** Last received message */
  lastMessage: WebSocketEvent | null;
  /** Send a message through the WebSocket */
  sendMessage: (message: unknown) => void;
  /** Manually reconnect */
  reconnect: () => void;
  /** Close the connection */
  disconnect: () => void;
}

// Default configuration
// Prefer an explicit VITE_WS_URL (dev), otherwise derive from the current host so
// the WebSocket is same-origin on whatever domain served the app (agent-mesh.org
// or app.agent-mesh.org).
const DEFAULT_WS_URL =
  import.meta.env.VITE_WS_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
    : 'ws://localhost:8888');
const WS_ENDPOINT = '/v1/ws';
const DEFAULT_RECONNECT_INTERVAL = 3000;
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
const DEFAULT_HEARTBEAT_INTERVAL = 30000;

/**
 * Hook for managing WebSocket connections with JWT authentication
 *
 * @param options - Configuration options for the WebSocket connection
 * @returns WebSocket state and control functions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isConnected, connectionState, lastMessage, sendMessage } = useWebSocket({
 *     onMessage: (event) => console.log('Received:', event),
 *     onConnect: () => console.log('Connected!'),
 *   });
 *
 *   return <div>Status: {connectionState}</div>;
 * }
 * ```
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    reconnect: shouldReconnect = true,
    reconnectInterval = DEFAULT_RECONNECT_INTERVAL,
    maxReconnectAttempts = DEFAULT_MAX_RECONNECT_ATTEMPTS,
    heartbeatInterval = DEFAULT_HEARTBEAT_INTERVAL,
  } = options;

  const isSignedIn = useAuthStore((state) => state.isAuthenticated);

  // WebSocket reference
  const wsRef = useRef<WebSocket | null>(null);

  // Reconnection state
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Heartbeat state
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setInterval>>();

  // Store callbacks in refs to avoid dependency changes
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
  }, [onMessage, onConnect, onDisconnect, onError]);

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketEvent | null>(null);

  /**
   * Start heartbeat mechanism to keep connection alive
   */
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearInterval(heartbeatTimeoutRef.current);
    }

    heartbeatTimeoutRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, heartbeatInterval);
  }, [heartbeatInterval]);

  /**
   * Stop heartbeat mechanism
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearInterval(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = undefined;
    }
  }, []);

  /**
   * Connect to the WebSocket server
   */
  const connect = useCallback(async () => {
    // Don't connect if not signed in
    if (!isSignedIn) {
      setConnectionState('disconnected');
      return;
    }

    try {
      // Get JWT access token for authentication
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setConnectionState('error');
        console.error('WebSocket: Failed to get authentication token');
        return;
      }

      setConnectionState('connecting');

      // Build WebSocket URL with token
      const wsUrl = `${DEFAULT_WS_URL}${WS_ENDPOINT}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;
        startHeartbeat();
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketEvent;
          setLastMessage(data);
          onMessageRef.current?.(data);
        } catch (error) {
          console.error('WebSocket: Failed to parse message:', error);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setConnectionState('disconnected');
        stopHeartbeat();
        onDisconnectRef.current?.();

        // Attempt reconnection if enabled and not a clean close
        if (shouldReconnect && !event.wasClean) {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current++;
            // Exponential backoff: delay = base * 2^(attempt-1)
            const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 1);
            console.log(`WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
            reconnectTimeoutRef.current = setTimeout(connect, delay);
          } else {
            console.warn('WebSocket: Max reconnection attempts reached');
            setConnectionState('error');
          }
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket: Connection error:', error);
        setConnectionState('error');
        onErrorRef.current?.(error);
      };
    } catch (error) {
      console.error('WebSocket: Failed to connect:', error);
      setConnectionState('error');
    }
  }, [
    isSignedIn,
    shouldReconnect,
    reconnectInterval,
    maxReconnectAttempts,
    startHeartbeat,
    stopHeartbeat,
  ]);

  /**
   * Send a message through the WebSocket
   */
  const sendMessage = useCallback((message: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket: Cannot send message - connection not open');
    }
  }, []);

  /**
   * Manually trigger a reconnection
   */
  const manualReconnect = useCallback(() => {
    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Reset reconnection attempts
    reconnectAttemptsRef.current = 0;

    // Connect
    connect();
  }, [connect]);

  /**
   * Close the WebSocket connection
   */
  const disconnect = useCallback(() => {
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Stop heartbeat
    stopHeartbeat();

    // Close connection
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setConnectionState('disconnected');
  }, [stopHeartbeat]);

  // Connect on mount and when sign-in status changes
  useEffect(() => {
    if (isSignedIn) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      stopHeartbeat();
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
    };
  }, [isSignedIn, connect, disconnect, stopHeartbeat]);

  return {
    isConnected,
    connectionState,
    lastMessage,
    sendMessage,
    reconnect: manualReconnect,
    disconnect,
  };
}

export default useWebSocket;
